// src/components/TransferList/TransferList.jsx
import React, { useMemo, useState } from "react";

import "./TransferList.css";

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function filterItems(items, search, groupFilter, getGroup, getLabel, getSubLabel) {
  const term = normalizeText(search);

  return items.filter((item) => {
    if (groupFilter && getGroup && getGroup(item) !== groupFilter) return false;
    if (!term) return true;

    const label = normalizeText(getLabel(item));
    const sub = normalizeText(getSubLabel(item));
    return label.includes(term) || sub.includes(term);
  });
}

function groupByTree(items, getTreeGroupLabel) {
  const map = new Map();
  (items || []).forEach((item) => {
    const label = String(getTreeGroupLabel(item) || "Sem grupo");
    if (!map.has(label)) map.set(label, []);
    map.get(label).push(item);
  });
  return Array.from(map.entries()).map(([label, children]) => ({ label, children }));
}

export default function TransferList({
  titleLeft = "Pendentes",
  titleRight = "Selecionados",
  selectedCountLabel = "Selecionados",
  itemsLeft = [],
  itemsRight = [],
  onMoveRight,
  onMoveLeft,
  filterLabel = "Setor",
  filterOptions = [],
  getItemLabel = (item) => item?.label || item?.nome || "",
  getItemSubLabel = (item) => item?.subLabel || item?.descricao || "",
  getItemGroup = (item) => item?.setorId || item?.groupId || "",
  treeMode = false,
  getTreeGroupLabel = (item) => getItemGroup(item) || "Sem grupo",
  pageSize = 50,
  hasMoreLeft = false,
  hasMoreRight = false,
  onLoadMoreLeft,
  onLoadMoreRight,
}) {
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [leftPick, setLeftPick] = useState({});
  const [rightPick, setRightPick] = useState({});
  const [leftPage, setLeftPage] = useState(1);
  const [rightPage, setRightPage] = useState(1);

  const filteredLeft = useMemo(
    () => filterItems(itemsLeft, leftSearch, groupFilter, getItemGroup, getItemLabel, getItemSubLabel),
    [itemsLeft, leftSearch, groupFilter, getItemGroup, getItemLabel, getItemSubLabel]
  );

  const filteredRight = useMemo(
    () => filterItems(itemsRight, rightSearch, groupFilter, getItemGroup, getItemLabel, getItemSubLabel),
    [itemsRight, rightSearch, groupFilter, getItemGroup, getItemLabel, getItemSubLabel]
  );

  const leftVisible = filteredLeft.slice(0, leftPage * pageSize);
  const rightVisible = filteredRight.slice(0, rightPage * pageSize);

  const leftTree = useMemo(
    () => (treeMode ? groupByTree(leftVisible, getTreeGroupLabel) : []),
    [treeMode, leftVisible, getTreeGroupLabel]
  );

  const rightTree = useMemo(
    () => (treeMode ? groupByTree(rightVisible, getTreeGroupLabel) : []),
    [treeMode, rightVisible, getTreeGroupLabel]
  );

  const selectedLeftItems = leftVisible.filter((item) => leftPick[item.id]);
  const selectedRightItems = rightVisible.filter((item) => rightPick[item.id]);

  const moveRight = (items) => {
    if (!onMoveRight || items.length === 0) return;
    onMoveRight(items);
    setLeftPick({});
  };

  const moveLeft = (items) => {
    if (!onMoveLeft || items.length === 0) return;
    onMoveLeft(items);
    setRightPick({});
  };

  const moveAllFilteredLeft = () => {
    if (!onMoveRight || filteredLeft.length === 0) return;
    onMoveRight(filteredLeft);
    setLeftPick({});
  };

  const moveAllFilteredRight = () => {
    if (!onMoveLeft || filteredRight.length === 0) return;
    onMoveLeft(filteredRight);
    setRightPick({});
  };

  const handleLoadMoreLeft = () => {
    if (leftVisible.length < filteredLeft.length) {
      setLeftPage((p) => p + 1);
      return;
    }
    if (hasMoreLeft && onLoadMoreLeft) {
      onLoadMoreLeft();
    }
  };

  const handleLoadMoreRight = () => {
    if (rightVisible.length < filteredRight.length) {
      setRightPage((p) => p + 1);
      return;
    }
    if (hasMoreRight && onLoadMoreRight) {
      onLoadMoreRight();
    }
  };

  return (
    <div className="transfer-list">
      <div className="transfer-summary">
        <strong>{selectedCountLabel}: {itemsRight.length}</strong>
        {filterOptions.length > 0 && (
          <label className="transfer-filter">
            {filterLabel}
            <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
              <option value="">Todos</option>
              {filterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="transfer-dual-wrap">
        <div className="transfer-box">
          <div className="transfer-box-head">
            <div>
              <b>{titleLeft}</b> <span className="transfer-count">({filteredLeft.length})</span>
            </div>
            <small className="transfer-muted">Clique para selecionar, duplo clique para mover</small>
          </div>
          <div className="transfer-toolbar">
            <input
              type="text"
              placeholder="Buscar..."
              value={leftSearch}
              onChange={(e) => {
                setLeftSearch(e.target.value);
                setLeftPage(1);
              }}
            />
          </div>
          <div className="transfer-list-body">
            {treeMode ? (
              leftTree.map((group) => (
                <div key={`left-${group.label}`} className="transfer-tree-group">
                  <div className="transfer-tree-group-title">{group.label}</div>
                  {group.children.map((item) => (
                    <div
                      key={item.id}
                      className={`transfer-item transfer-item-child ${leftPick[item.id] ? "focus" : ""}`}
                      onClick={() => setLeftPick((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                      onDoubleClick={() => moveRight([item])}
                      role="button"
                      tabIndex={0}
                    >
                      <label className="transfer-item-left" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!leftPick[item.id]}
                          onChange={() => setLeftPick((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                        />
                        <span className="transfer-item-title">{getItemLabel(item)}</span>
                      </label>
                      <div className="transfer-item-right">
                        <span className="transfer-mini">{getItemSubLabel(item)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              leftVisible.map((item) => (
                <div
                  key={item.id}
                  className={`transfer-item ${leftPick[item.id] ? "focus" : ""}`}
                  onClick={() => setLeftPick((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                  onDoubleClick={() => moveRight([item])}
                  role="button"
                  tabIndex={0}
                >
                  <label className="transfer-item-left" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!leftPick[item.id]}
                      onChange={() => setLeftPick((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                    />
                    <span className="transfer-item-title">{getItemLabel(item)}</span>
                  </label>
                  <div className="transfer-item-right">
                    <span className="transfer-mini">{getItemSubLabel(item)}</span>
                  </div>
                </div>
              ))
            )}
            {leftVisible.length === 0 && (
              <div className="transfer-empty">Nenhum item pendente</div>
            )}
            {(leftVisible.length < filteredLeft.length || hasMoreLeft) && (
              <button type="button" className="transfer-more" onClick={handleLoadMoreLeft}>
                Carregar mais
              </button>
            )}
          </div>
        </div>

        <div className="transfer-move">
          <button type="button" className="btn" onClick={() => moveRight(selectedLeftItems)} title="Mover selecionados">
            ▶
          </button>
          <button type="button" className="btn" onClick={moveAllFilteredLeft} title="Mover todos">
            ▶▶
          </button>
          <button type="button" className="btn" onClick={() => moveLeft(selectedRightItems)} title="Remover selecionados">
            ◀
          </button>
          <button type="button" className="btn" onClick={moveAllFilteredRight} title="Remover todos">
            ◀◀
          </button>
        </div>

        <div className="transfer-box">
          <div className="transfer-box-head">
            <div>
              <b>{titleRight}</b> <span className="transfer-count">({filteredRight.length})</span>
            </div>
            <small className="transfer-muted">Clique para selecionar, duplo clique para remover</small>
          </div>
          <div className="transfer-toolbar">
            <input
              type="text"
              placeholder="Buscar..."
              value={rightSearch}
              onChange={(e) => {
                setRightSearch(e.target.value);
                setRightPage(1);
              }}
            />
          </div>
          <div className="transfer-list-body">
            {treeMode ? (
              rightTree.map((group) => (
                <div key={`right-${group.label}`} className="transfer-tree-group">
                  <div className="transfer-tree-group-title">{group.label}</div>
                  {group.children.map((item) => (
                    <div
                      key={item.id}
                      className={`transfer-item transfer-item-child ${rightPick[item.id] ? "focus" : ""}`}
                      onClick={() => setRightPick((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                      onDoubleClick={() => moveLeft([item])}
                      role="button"
                      tabIndex={0}
                    >
                      <label className="transfer-item-left" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!rightPick[item.id]}
                          onChange={() => setRightPick((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                        />
                        <span className="transfer-item-title">{getItemLabel(item)}</span>
                      </label>
                      <div className="transfer-item-right">
                        <span className="transfer-mini">{getItemSubLabel(item)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              rightVisible.map((item) => (
                <div
                  key={item.id}
                  className={`transfer-item ${rightPick[item.id] ? "focus" : ""}`}
                  onClick={() => setRightPick((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                  onDoubleClick={() => moveLeft([item])}
                  role="button"
                  tabIndex={0}
                >
                  <label className="transfer-item-left" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!rightPick[item.id]}
                      onChange={() => setRightPick((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                    />
                    <span className="transfer-item-title">{getItemLabel(item)}</span>
                  </label>
                  <div className="transfer-item-right">
                    <span className="transfer-mini">{getItemSubLabel(item)}</span>
                  </div>
                </div>
              ))
            )}
            {rightVisible.length === 0 && (
              <div className="transfer-empty">Nenhum item selecionado</div>
            )}
            {(rightVisible.length < filteredRight.length || hasMoreRight) && (
              <button type="button" className="transfer-more" onClick={handleLoadMoreRight}>
                Carregar mais
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
